import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Delta, LiveDot, Marquee, Price, TokenMark } from '@/components/ui';
import { useMarket } from '@/providers/market';
import { cn } from '@/lib/cn';
import { weiToNumber } from '@/lib/format';
import type { DemoToken } from '@/lib/demo/types';

/** How many tape entries the strip carries. */
const TICKER_LIMIT = 18;

function TickerEntry({ token }: { token: DemoToken }) {
  const spot = weiToNumber(token.spotPrice);
  return (
    <Link
      to={`/token/${token.address}`}
      className="flex h-9 shrink-0 items-center gap-2 border-r border-line/70 px-3 transition-colors duration-200 ease-swift hover:bg-elevated/70"
    >
      <TokenMark symbol={token.symbol} hue={token.hue} glyph={token.glyph} size="xs" />
      <span className="font-mono text-2xs font-semibold uppercase text-fg">{token.symbol}</span>
      <Price value={spot} className="text-2xs text-muted" />
      <Delta value={token.change24h} digits={2} className="text-2xs" />
    </Link>
  );
}

/**
 * Full-bleed terminal tape under the header: a seamless marquee of the most
 * traded tokens, prefixed by a fixed LIVE cap so it reads as a feed, not a promo.
 */
export function TickerBar({ className }: { className?: string }) {
  const { tokens, ready } = useMarket();

  const entries = useMemo(
    () => [...tokens].sort((a, b) => b.volume24hEth - a.volume24hEth).slice(0, TICKER_LIMIT),
    [tokens],
  );

  return (
    <div className={cn('relative w-full overflow-hidden border-y border-line bg-surface/70', className)}>
      <div className="flex h-9 items-stretch">
        <div className="flex shrink-0 items-center gap-1.5 border-r border-line bg-elevated px-3">
          <LiveDot tone="up" />
          <span className="text-3xs font-semibold uppercase tracking-[0.14em] text-muted">Live</span>
        </div>

        {/* Trailing fade only: a leading fade would dim the entry sitting flush
            against the LIVE cap and read as a clipped first item. */}
        {ready && entries.length > 0 ? (
          <Marquee speed={64} pauseOnHover fade="right" className="min-w-0 flex-1">
            <div className="flex items-stretch">
              {entries.map((token) => (
                <TickerEntry key={token.address} token={token} />
              ))}
            </div>
          </Marquee>
        ) : (
          <div className="flex min-w-0 flex-1 items-center px-3">
            <span className="text-2xs text-subtle">Loading market tape…</span>
          </div>
        )}
      </div>
    </div>
  );
}
